<template>
	<v-card :class="$vuetify.breakpoint.xs ? 'ma-1' : 'ma-5'" flat :loading="loading">
		<v-card-title class="pb-0 d-flex align-center">
			<span>{{ $t("twilio-overview-title") }}</span>
			<v-spacer />
			<v-btn icon @click="$emit('go-to-detail')">
				<v-icon>mdi-arrow-right</v-icon>
			</v-btn>
		</v-card-title>

		<div v-if="organizationsSummary.length > 0" class="health-summary px-4 pb-2">
			<span class="health-chip health-chip-ok">
				<v-icon x-small color="green" class="mr-1">mdi-check-circle</v-icon>
				{{ healthCounts.ok }} ({{ healthCounts.okPct }}%)
			</span>
			<span class="health-chip health-chip-warning">
				<v-icon x-small color="amber" class="mr-1">mdi-alert-outline</v-icon>
				{{ healthCounts.warning }} ({{ healthCounts.warningPct }}%)
			</span>
			<span class="health-chip health-chip-error">
				<v-icon x-small color="red" class="mr-1">mdi-alert-circle</v-icon>
				{{ healthCounts.critical }} ({{ healthCounts.criticalPct }}%)
			</span>
		</div>

		<v-divider />

		<!-- Error message -->
		<v-alert
			v-if="errorMessage"
			type="error"
			dense
			text
			class="ma-4"
		>
			{{ errorMessage }}
		</v-alert>

		<!-- Overview Content -->
		<v-card-text v-if="!errorMessage" :class="$vuetify.breakpoint.xs ? 'pa-0' : 'pa-0'" :style="overviewContentStyle">
			<!-- Mobile: Compact List -->
			<template v-if="$vuetify.breakpoint.xs">
				<table class="mobile-table">
					<thead class="sticky-header">
						<tr>
							<th class="mobile-th-org">
								Org
							</th>
							<th
								v-for="(range, idx) in timeRanges"
								:key="range.key"
								class="mobile-th-range"
								:style="idx % 2 === 0 ? 'background-color: #f0f0f0' : ''"
								@click="setSortRange(range.key)"
							>
								{{ range.label.replace('Last ', '') }}
								<v-icon v-if="sortRange === range.key" x-small>{{ sortDirection === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="(org, idx) in sortedOrganizations"
							:key="org.id"
							class="mobile-tr"
							:class="{ 'mobile-tr-critical': getOrgSeverity(org) === 'critical', 'mobile-tr-even': idx % 2 === 1 }"
							@click="selectOrganization(org.id)"
						>
							<td class="mobile-td-org">
								<div class="mobile-org-inner">
									<v-icon :color="getOrgHealthColor(org)" x-small class="mr-1 flex-shrink-0">{{ getOrgHealthIcon(org) }}</v-icon>
									<span class="mobile-org-name">{{ organizationName(org.displayName) }}</span>
								</div>
							</td>
							<td
								v-for="(range, rangeIdx) in timeRanges"
								:key="range.key"
								class="mobile-td-range"
								:class="{ 'col-striped': rangeIdx % 2 === 0 }"
							>
								<div v-if="getTotalByRange(org, range.key) > 0" class="vertical-bars vertical-bars-mobile">
									<div v-if="getCountByType(org, range.key, 'critical') > 0" class="vbar-row">
										<div class="vbar vbar-critical" :style="{ width: getBarWidthPercent(org, range.key, 'critical') + '%' }">
											<span class="vbar-label">{{ getCountByType(org, range.key, 'critical') }}</span>
										</div>
									</div>
									<div v-if="getCountByType(org, range.key, 'error') > 0" class="vbar-row">
										<div class="vbar vbar-error" :style="{ width: getBarWidthPercent(org, range.key, 'error') + '%' }">
											<span class="vbar-label">{{ getCountByType(org, range.key, 'error') }}</span>
										</div>
									</div>
									<div v-if="getCountByType(org, range.key, 'warning') > 0" class="vbar-row">
										<div class="vbar vbar-warning" :style="{ width: getBarWidthPercent(org, range.key, 'warning') + '%' }">
											<span class="vbar-label">{{ getCountByType(org, range.key, 'warning') }}</span>
										</div>
									</div>
								</div>
								<span v-else class="grey--text" style="font-size: 10px">—</span>
							</td>
						</tr>
					</tbody>
				</table>
			</template>

			<!-- Desktop: Table Layout -->
			<v-simple-table v-if="!$vuetify.breakpoint.xs" dense fixed-header height="60vh">
				<template #default>
					<thead>
						<tr>
							<th>Organization</th>
							<th
								v-for="(range, idx) in timeRanges"
								:key="range.key"
								class="text-center sortable-header"
								:class="{ 'col-striped': idx % 2 === 0 }"
								@click="setSortRange(range.key)"
							>
								{{ range.label }}
								<v-icon v-if="sortRange === range.key" x-small class="ml-1">{{ sortDirection === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
							</th>
							<th style="width: 32px" />
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="(org, idx) in sortedOrganizations"
							:key="org.id"
							:class="{ 'org-row-critical': getOrgSeverity(org) === 'critical', 'org-row-even': idx % 2 === 1 }"
							class="org-row"
							@click="selectOrganization(org.id)"
						>
							<td>
								<div class="d-flex align-center">
									<v-icon :color="getOrgHealthColor(org)" small class="mr-2">
										{{ getOrgHealthIcon(org) }}
									</v-icon>
									<span class="font-weight-medium">
										{{ organizationName(org.displayName) }}
									</span>
								</div>
							</td>
							<td
								v-for="(range, rangeIdx) in timeRanges"
								:key="range.key"
								class="text-center"
								:class="{ 'col-striped': rangeIdx % 2 === 0 }"
							>
								<div v-if="getTotalByRange(org, range.key) > 0" class="vertical-bars vertical-bars-desktop">
									<div v-if="getCountByType(org, range.key, 'critical') > 0" class="vbar-row">
										<div class="vbar vbar-critical" :style="{ width: getBarWidthPercent(org, range.key, 'critical') + '%' }">
											<span class="vbar-label">{{ getCountByType(org, range.key, 'critical') }}</span>
										</div>
									</div>
									<div v-if="getCountByType(org, range.key, 'error') > 0" class="vbar-row">
										<div class="vbar vbar-error" :style="{ width: getBarWidthPercent(org, range.key, 'error') + '%' }">
											<span class="vbar-label">{{ getCountByType(org, range.key, 'error') }}</span>
										</div>
									</div>
									<div v-if="getCountByType(org, range.key, 'warning') > 0" class="vbar-row">
										<div class="vbar vbar-warning" :style="{ width: getBarWidthPercent(org, range.key, 'warning') + '%' }">
											<span class="vbar-label">{{ getCountByType(org, range.key, 'warning') }}</span>
										</div>
									</div>
								</div>
								<span v-else class="grey--text text-caption">—</span>
							</td>
							<td class="text-center" style="width: 32px; padding: 0">
								<v-icon small>mdi-chevron-right</v-icon>
							</td>
						</tr>
					</tbody>
				</template>
			</v-simple-table>

			<div v-if="!organizationsSummary || organizationsSummary.length === 0" class="text-body-2 grey--text text-center py-6">
				{{ $t("twilio-overview-no-organizations") }}
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
const TIME_RANGES = {
	last1h: "1h",
	last24h: "24h",
	last72h: "72h",
	last30d: "30d"
};

export default {
	name: "TwilioOverviewPanel",

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			loading: false,
			errorMessage: "",
			organizationsSummary: [],
			sortRange: "last30d",
			sortDirection: "desc",
			timeRanges: [
				{ key: "last1h", label: "Last 1h" },
				{ key: "last24h", label: "Last 24h" },
				{ key: "last72h", label: "Last 72h" },
				{ key: "last30d", label: "Last 30d" }
			]
		};
	},

	computed: {
		overviewContentStyle() {
			if (this.$vuetify.breakpoint.xs) {
				return { overflowY: "auto", flex: "1" };
			}
			return {};
		},

		sortedOrganizations() {
			const dir = this.sortDirection === "desc" ? 1 : -1;
			const range = this.sortRange;

			return [...this.organizationsSummary].sort((a, b) => {
				const types = ["critical", "error", "warning"];

				for (const type of types) {
					const diff = this.getCountByType(b, range, type) - this.getCountByType(a, range, type);

					if (diff !== 0) return diff * dir;
				}
				return 0;
			});
		},

		healthCounts() {
			const total = this.organizationsSummary.length;

			if (!total) return { ok: 0, warning: 0, critical: 0, okPct: 0, warningPct: 0, criticalPct: 0 };

			let ok = 0, warning = 0, critical = 0;

			for (const org of this.organizationsSummary) {
				const severity = this.getOrgSeverity(org);

				if (severity === "critical") critical++;
				else if (severity === "warning") warning++;
				else ok++;
			}

			return {
				ok,
				warning,
				critical,
				okPct: Math.round((ok / total) * 100),
				warningPct: Math.round((warning / total) * 100),
				criticalPct: Math.round((critical / total) * 100)
			};
		}
	},

	async created() {
		await this.fetchOrganizationsSummary();
	},

	methods: {
		async fetchOrganizationsSummary() {
			this.loading = true;
			this.errorMessage = "";

			try {
				// Fetch all organizations and their summary data in parallel
				const { data } = await this.axios.get("/devops/twilio/summary");

				this.organizationsSummary = data.organizations || [];
			} catch (err) {
				console.error("Error fetching Twilio organizations summary:", err);
				this.errorMessage = this.$t("twilio-overview-fetch-error") || "Failed to fetch summary";
			} finally {
				this.loading = false;
			}
		},

		selectOrganization(orgId) {
			this.$emit("select-organization", orgId);
		},

		toggleSortDirection() {
			this.sortDirection = this.sortDirection === "desc" ? "asc" : "desc";
		},

		setSortRange(range) {
			if (this.sortRange === range) {
				this.toggleSortDirection();
			} else {
				this.sortRange = range;
			}
		},

		getCountByType(org, range, type) {
			const rangeData = org.summary?.[range];

			if (!rangeData) return 0;

			if (type === "critical") {
				return (rangeData.errors || []).filter(e => this.isCriticalErrorCode(e.code)).reduce((sum, e) => sum + e.count, 0);
			}
			if (type === "error") {
				return (rangeData.errors || []).filter(e => e.level === "ERROR" && !this.isCriticalErrorCode(e.code)).reduce((sum, e) => sum + e.count, 0);
			}
			if (type === "warning") {
				return (rangeData.errors || []).filter(e => e.level === "WARNING").reduce((sum, e) => sum + e.count, 0);
			}
			return 0;
		},

		getTotalByRange(org, range) {
			return this.getCountByType(org, range, "critical") +
				   this.getCountByType(org, range, "error") +
				   this.getCountByType(org, range, "warning");
		},

		getBarPercent(org, range, type) {
			const total = this.getTotalByRange(org, range);

			if (!total) return 0;
			return Math.round((this.getCountByType(org, range, type) / total) * 100);
		},

		getBarWidthPercent(org, range, type) {
			const total = this.getTotalByRange(org, range);

			if (!total) return 0;
			return Math.round((this.getCountByType(org, range, type) / total) * 100);
		},

		isCriticalErrorCode(code) {
			return code === "32011" || code === "32007" || code === "32009";
		},

		getOrgSeverity(org) {
			if (this.getCountByType(org, "last1h", "critical") > 0) return "critical";
			if (this.getCountByType(org, "last24h", "critical") > 0) return "critical";
			if (this.getCountByType(org, "last72h", "critical") > 0) return "warning";
			return "ok";
		},

		getOrgHealthColor(org) {
			const severity = this.getOrgSeverity(org);

			switch (severity) {
				case "critical": return "red";
				case "error": return "orange";
				case "warning": return "amber";
				default: return "green";
			}
		},

		getOrgHealthIcon(org) {
			const severity = this.getOrgSeverity(org);

			switch (severity) {
				case "critical": return "mdi-alert-circle";
				case "error": return "mdi-alert";
				case "warning": return "mdi-alert-outline";
				default: return "mdi-check-circle";
			}
		},

		organizationName(name) {
			if (!name) return "";
			if (typeof name === "string") return name;
			const [locale] = Object.keys(name);

			return name[locale];
		}
	}
};
</script>

<style scoped>
/* Mobile table */
.mobile-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}

.mobile-table thead {
	background: white;
}

.mobile-th-org {
	text-align: left;
	font-size: 11px;
	font-weight: 600;
	padding: 4px 6px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	width: 40%;
}

.mobile-th-range {
	text-align: center;
	font-size: 10px;
	font-weight: 600;
	padding: 4px 2px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	cursor: pointer;
	user-select: none;
	width: 15%;
}

.mobile-tr {
	cursor: pointer;
	border-left: 3px solid transparent;
}

.mobile-tr:active {
	background-color: rgba(0, 0, 0, 0.04);
}

.mobile-tr-critical {
	border-left-color: #f44336;
	background-color: rgba(244, 67, 54, 0.03);
}

.mobile-tr-critical td.col-striped {
	background-color: rgba(244, 67, 54, 0.06);
}

.mobile-tr-even {
	background-color: #fafafa;
}

.mobile-tr-even td.col-striped {
	background-color: #efefef;
}

.mobile-tr-critical.mobile-tr-even {
	background-color: rgba(244, 67, 54, 0.06);
}

.mobile-tr-critical.mobile-tr-even td.col-striped {
	background-color: rgba(244, 67, 54, 0.09);
}

.mobile-td-org {
	padding: 6px 6px;
	vertical-align: middle;
	overflow: hidden;
}

.mobile-org-inner {
	display: flex;
	align-items: center;
	min-width: 0;
}

.mobile-td-range {
	text-align: center;
	padding: 6px 2px;
	vertical-align: middle;
}

.stacked-bar-mobile {
	width: 100%;
	height: 14px;
	min-width: unset;
	border-radius: 2px;
}

.stacked-bar-mobile .bar-label {
	font-size: 8px;
}

/* Vertical bars */
.vertical-bars {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 60px;
	padding: 8px 0;
}

.vertical-bars-desktop {
	width: 100%;
	min-width: 80px;
	max-width: 140px;
	margin: 0 auto;
}

.vertical-bars-mobile {
	width: 100%;
	min-width: unset;
}

.vbar-row {
	width: 100%;
	height: 14px;
	background: #f5f5f5;
	border-radius: 2px;
	position: relative;
}

.vbar {
	height: 100%;
	display: flex;
	align-items: center;
	min-width: 4px;
	border-radius: 2px;
	transition: width 0.3s ease;
	position: relative;
}

.vbar-label {
	position: absolute;
	left: 0;
	top: 0;
	height: 100%;
	display: flex;
	align-items: center;
	font-size: 8px;
	font-weight: 700;
	color: white;
	line-height: 1;
	text-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
	white-space: nowrap;
	pointer-events: none;
	background: inherit;
	padding: 0 4px;
	border-radius: 2px;
}

.vbar-critical {
	background: #e53935;
}

.vbar-error {
	background: #fb8c00;
}

.vbar-warning {
	background: #fdd835;
}

.vbar-warning .vbar-label {
	color: #5d4037;
	text-shadow: none;
}

/* Stacked bar (legacy) */
.stacked-bar {
	display: flex;
	height: 18px;
	border-radius: 3px;
	overflow: hidden;
	background: #f5f5f5;
	min-width: 60px;
}

.stacked-bar-desktop {
	width: 100%;
	min-width: 80px;
	max-width: 140px;
	margin: 0 auto;
}

.bar-segment {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 4px;
	transition: width 0.3s ease;
}

.bar-label {
	font-size: 9px;
	font-weight: 700;
	color: white;
	line-height: 1;
	text-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
}

.bar-critical {
	background: #e53935;
}

.bar-error {
	background: #fb8c00;
}

.bar-warning {
	background: #fdd835;
}

.bar-warning .bar-label {
	color: #5d4037;
	text-shadow: none;
}

/* Desktop table */
::v-deep .org-row td {
	padding-top: 8px;
	padding-bottom: 8px;
}

.org-row {
	cursor: pointer;
	transition: background-color 0.2s ease;
}

::v-deep .org-row-even td {
	background-color: #fafafa;
}

::v-deep .org-row-even td.col-striped {
	background-color: #efefef;
}

::v-deep .org-row:hover td {
	background-color: rgba(25, 118, 210, 0.08) !important;
}

::v-deep .org-row-critical td {
	background-color: rgba(244, 67, 54, 0.12) !important;
}

::v-deep .org-row-critical.org-row-even td {
	background-color: rgba(244, 67, 54, 0.16) !important;
}

::v-deep .org-row-critical td.col-striped {
	background-color: rgba(244, 67, 54, 0.15) !important;
}

::v-deep .org-row-critical.org-row-even td.col-striped {
	background-color: rgba(244, 67, 54, 0.19) !important;
}

::v-deep .org-row-critical:hover td {
	background-color: rgba(244, 67, 54, 0.22) !important;
}

::v-deep .org-row-critical:hover td.col-striped {
	background-color: rgba(244, 67, 54, 0.25) !important;
}

.col-striped {
	background-color: #f0f0f0;
}

.sortable-header {
	cursor: pointer;
	user-select: none;
}

.sortable-header:hover {
	background-color: rgba(0, 0, 0, 0.04);
}

.sticky-header {
	position: sticky;
	top: 0;
	z-index: 2;
	background: white;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

::v-deep .v-data-table--fixed-header thead th {
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.mobile-org-name {
	font-size: 11px;
	font-weight: 600;
	word-break: break-word;
	min-width: 0;
	flex: 1;
}

.health-summary {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
}

.health-chip {
	display: inline-flex;
	align-items: center;
	font-size: 12px;
	font-weight: 600;
	color: #616161;
}
</style>
